/* Keeps the free-shipping progress current. The server renders the first state; this updates it
   after cart changes (and variant changes on product pages) without reloading the section. */
if (!customElements.get('shipping-progress')) {
  let cartRequest = null;

  // Instances that refresh in the same event dispatch share one cart request. A later change always
  // fetches afresh, so a response that was already in flight can never describe an older cart.
  const fetchCart = (url) => {
    if (!cartRequest) {
      cartRequest = fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      });
      Promise.resolve().then(() => { cartRequest = null; });
    }
    return cartRequest;
  };

  customElements.define(
    'shipping-progress',
    class ShippingProgress extends HTMLElement {
      connectedCallback() {
        this.goal = Number(this.dataset.goal);
        this.currency = this.dataset.currency;
        this.counted = Number(this.dataset.cartTotal);
        this.itemCount = Number(this.dataset.itemCount);
        this.variantPrice = Number(this.dataset.variantPrice) || 0;
        this.requestNumber = 0;
        this.text = this.querySelector('.shipping-progress__text');
        this.fill = this.querySelector('.shipping-progress__fill');
        this.track = this.querySelector('.shipping-progress__track');
        this.unsubscribers = [];

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          if (PUB_SUB_EVENTS.cartUpdate) this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh()));
          if (this.dataset.context === 'product' && PUB_SUB_EVENTS.variantChange) {
            this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.variantChanged(event)));
          }
        }
        this.onPageShow = (event) => { if (event.persisted) this.refresh(); };
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        this.requestNumber += 1;
        this.unsubscribers.forEach((unsubscribe) => typeof unsubscribe === 'function' && unsubscribe());
        this.unsubscribers = [];
        window.removeEventListener('pageshow', this.onPageShow);
      }

      money(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'cs', {
          style: 'currency', currency: this.currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
        }).format(cents / 100);
      }

      message(key, amount) {
        const escape = (value) => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return (this.dataset[key] || '').replace('__AMOUNT__', escape(amount));
      }

      refresh() {
        const current = ++this.requestNumber;
        return fetchCart(this.dataset.cartUrl)
          .then((cart) => {
            if (current !== this.requestNumber) return;
            // A different currency or an invalid total must never produce a promise; keep the last good state.
            if (cart.currency !== this.currency || !Number.isFinite(cart.total_price) || cart.total_price < 0) return;
            this.counted = cart.total_price;
            this.itemCount = Number(cart.item_count) || 0;
            this.render();
          })
          .catch(() => {});
      }

      variantChanged(event) {
        const sectionId = event?.data?.sectionId;
        if (this.dataset.sectionId && sectionId && sectionId !== this.dataset.sectionId) return;
        const price = Number(event?.data?.variant?.price);
        this.variantPrice = Number.isFinite(price) && price > 0 ? price : 0;
        this.render();
      }

      render() {
        if (!Number.isFinite(this.goal) || this.goal <= 0) return;
        const product = this.dataset.context === 'product';
        const remaining = Math.max(0, this.goal - this.counted);
        let html;
        let state;
        if (this.counted >= this.goal) { state = 'reached'; html = this.message('msgReached', ''); }
        else if (product && this.variantPrice > 0 && this.counted + this.variantPrice >= this.goal) { state = 'with_product'; html = this.message('msgWithProduct', ''); }
        else if (this.itemCount === 0) { state = 'goal'; html = this.message('msgGoal', this.money(this.goal)); }
        else { state = 'remaining'; html = this.message('msgRemaining', this.money(remaining)); }
        this.dataset.state = state;
        if (this.text) this.text.innerHTML = html;

        const percent = Math.min(100, Math.floor((this.counted * 100) / this.goal));
        if (this.fill) this.fill.style.width = `${percent}%`;
        if (this.track) this.track.setAttribute('aria-valuenow', String(Math.min(Math.floor(this.counted / 100), Math.floor(this.goal / 100))));
      }
    }
  );
}
